import {
  CalendarToday,
  Close as CloseIcon,
  CreditCard,
  Description,
  Discount,
  LocalShipping,
  Payment,
  RateReview,
  Receipt,
  ShoppingCart,
} from "@mui/icons-material";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  Skeleton,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import { amazonColors } from "../../../config/amazonTheme";
import { formatApiError } from "../../../utils/formatApiError";
import {
  getItemStatuses,
  getOrders,
  ItemStatus,
} from "../../../services/orderService";
import { Order, OrderFilters, OrderItem } from "../../../types/order";
import Currency from "../../common/Currency";

import StatusTimeline from "./StatusTimeline";

const PAGE_SIZE = 20;
const DEFAULT_FILTER = "past_30_days";

// --- Helpers ---

const getDateRangeForFilter = (
  filterValue: string
): Pick<OrderFilters, "startDate" | "endDate"> => {
  const today = new Date();

  const endOfTodayUtc = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      23,
      59,
      59,
      999
    )
  );
  const startOfTodayUtc = new Date(
    Date.UTC(
      today.getUTCFullYear(),
      today.getUTCMonth(),
      today.getUTCDate(),
      0,
      0,
      0,
      0
    )
  );

  if (filterValue === "past_30_days") {
    const start = new Date(startOfTodayUtc);
    start.setUTCDate(start.getUTCDate() - 30);
    return {
      startDate: start.toISOString(),
      endDate: endOfTodayUtc.toISOString(),
    };
  }

  if (filterValue === "past_3_months") {
    const start = new Date(startOfTodayUtc);
    start.setUTCMonth(start.getUTCMonth() - 3);
    return {
      startDate: start.toISOString(),
      endDate: endOfTodayUtc.toISOString(),
    };
  }

  if (filterValue.startsWith("year_")) {
    const year = parseInt(filterValue.split("_")[1], 10);
    if (!Number.isNaN(year)) {
      const startOfYear = new Date(Date.UTC(year, 0, 1, 0, 0, 0, 0));
      const endOfYear = new Date(Date.UTC(year, 11, 31, 23, 59, 59, 999));
      return {
        startDate: startOfYear.toISOString(),
        endDate: endOfYear.toISOString(),
      };
    }
  }

  return {};
};

const normalizeStatusName = (value: string) =>
  value.toLowerCase().replace(/\s+/g, " ").trim();

const SPECIAL_ITEM_STATUSES = new Set(
  [
    "cancelled",
    "issue noticed",
    "feedback",
    "Returned",
    "Cargo Dropped Off",
    "Damaged",
    "Refunded",
  ].map(normalizeStatusName)
);

const isSpecialStatus = (statusName: string) =>
  SPECIAL_ITEM_STATUSES.has(normalizeStatusName(statusName));

const getStatusNames = (status: ItemStatus) =>
  [status.name, status.displayName]
    .filter((name): name is string => Boolean(name))
    .map(normalizeStatusName);

const findStatusByName = (statusName: string, allStatuses: ItemStatus[]) => {
  const normalizedName = normalizeStatusName(statusName);
  return allStatuses.find((status) =>
    getStatusNames(status).includes(normalizedName)
  );
};

const getEffectiveItemStatus = (
  item: OrderItem,
  orderStatus: string | undefined,
  allStatuses: ItemStatus[]
) => {
  const itemStatusName = item.itemStatusName || "";
  const itemIsSpecial = isSpecialStatus(itemStatusName);
  const orderStatusName = orderStatus || "";
  const orderIsSpecial = isSpecialStatus(orderStatusName);

  if (orderIsSpecial && !itemIsSpecial) {
    const matchedStatus = findStatusByName(orderStatusName, allStatuses);
    return {
      name:
        matchedStatus?.displayName || matchedStatus?.name || orderStatusName,
      id: matchedStatus?.id ?? item.itemStatusId,
      isSpecial: true,
    };
  }

  return {
    name: itemStatusName,
    id: item.itemStatusId,
    isSpecial: itemIsSpecial,
  };
};

const getVisibleItemStatuses = (
  itemStatusName: string,
  allStatuses: ItemStatus[],
  itemStatusId: number
) => {
  const normalizedItemStatus = normalizeStatusName(itemStatusName || "");
  const isSpecial = SPECIAL_ITEM_STATUSES.has(normalizedItemStatus);

  if (isSpecial) {
    return allStatuses.filter((status) => {
      if (status.id === itemStatusId) return true;
      return getStatusNames(status).includes(normalizedItemStatus);
    });
  }
  return allStatuses.filter(
    (status) =>
      !getStatusNames(status).some((name) => SPECIAL_ITEM_STATUSES.has(name))
  );
};

type StatusColor =
  | "success"
  | "warning"
  | "info"
  | "primary"
  | "error"
  | "default";

const getStatusColor = (status: string): StatusColor => {
  const normalized = (status || "").toLowerCase();
  if (["delivered"].includes(normalized)) return "success";
  if (["processing", "pending"].includes(normalized)) return "warning";
  if (["created", "confirmed", "in transit"].includes(normalized))
    return "info";
  if (
    ["receivedfromamazon", "shipped", "out for delivery"].includes(normalized)
  )
    return "primary";
  if (
    ["cancelled", "refunded", "failed delivery", "returned"].includes(
      normalized
    )
  )
    return "error";
  return "default";
};

const getPaymentStatusColor = (status: string): StatusColor => {
  const normalized = (status || "").toLowerCase();
  if (["succeeded", "paid", "completed"].includes(normalized)) return "success";
  if (["pending", "pending_verification", "processing"].includes(normalized)) return "warning";
  if (["failed", "declined", "cancelled", "canceled"].includes(normalized)) return "error";
  if (["refunded", "partially_refunded"].includes(normalized)) return "info";
  return "default";
};

const getPaymentStatusLabelKey = (status: string): string => {
  const normalized = (status || "").toLowerCase();
  if (["succeeded", "paid", "completed"].includes(normalized)) return "orders.paymentStatusLabels.paid";
  if (["pending_verification"].includes(normalized)) return "orders.paymentStatusLabels.pendingVerification";
  if (["pending", "processing"].includes(normalized)) return "orders.paymentStatusLabels.pending";
  if (["failed", "declined"].includes(normalized)) return "orders.paymentStatusLabels.failed";
  if (["cancelled", "canceled"].includes(normalized)) return "orders.paymentStatusLabels.cancelled";
  if (["refunded"].includes(normalized)) return "orders.paymentStatusLabels.refunded";
  if (["partially_refunded"].includes(normalized)) return "orders.paymentStatusLabels.partiallyRefunded";
  return "";
};

const formatDate = (dateString: string) => {
  if (!dateString) return "";
  try {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return dateString;
  }
};

// --- Reusable Order Item Component ---
interface OrderItemRowProps {
  item: OrderItem;
  itemStatuses: ItemStatus[];
  isDialog?: boolean;
  orderStatus?: string;
}

const OrderItemRow: React.FC<OrderItemRowProps> = React.memo(
  ({ item, itemStatuses, isDialog = false, orderStatus }) => {
    const { t } = useTranslation();
    const { effectiveStatus, visibleItemStatuses, showStatusChip } =
      useMemo(() => {
        const effective = getEffectiveItemStatus(
          item,
          orderStatus,
          itemStatuses
        );
        const visible = getVisibleItemStatuses(
          effective.name,
          itemStatuses,
          effective.id
        );
        const showChip =
          effective.name && (!effective.isSpecial || visible.length === 0);
        return {
          effectiveStatus: effective,
          visibleItemStatuses: visible,
          showStatusChip: showChip,
        };
      }, [item, orderStatus, itemStatuses]);

    // --- DIALOG LAYOUT ---
    if (isDialog) {
      return (
        <Box
          sx={{
            py: 2,
            borderBottom: "1px solid",
            borderColor: "grey.100",
            width: "100%",
          }}
        >
          <Grid container spacing={2} wrap="nowrap">
            {/* Image Column */}
            <Grid item>
              <Avatar
                src={item.image}
                variant="rounded"
                sx={{
                  width: 80,
                  height: 80,
                  bgcolor: "white",
                  border: "1px solid",
                  borderColor: "grey.200",
                  img: { objectFit: "contain", p: 0.5 },
                }}
              />
            </Grid>

            {/* Content Column */}
            <Grid item sx={{ flex: 1, minWidth: 0 }}>
              <Typography
                variant="subtitle2"
                component="div"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.4,
                  color: amazonColors.blue,
                  mb: 0.5,
                }}
              >
                {item.title}
              </Typography>

              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{ mb: 1 }}
              >
                {showStatusChip && (
                  <Chip
                    label={
                      <span className="notranslate" translate="no">
                        {effectiveStatus.name}
                      </span>
                    }
                    size="small"
                    color={getStatusColor(effectiveStatus.name)}
                    sx={{ height: 18, fontSize: "0.6rem", fontWeight: 700 }}
                  />
                )}
              </Stack>

              <Typography
                translate="no"
                variant="body2"
                fontWeight={700}
                color="text.primary"
                className="notranslate"
              >
                <Currency amount={item.price} />
                {item.sellerShippingFee != null && item.sellerShippingFee > 0 && (
                  <Box component="span" sx={{ ml: 0.5, fontWeight: 400, color: "text.secondary", fontSize: "0.8rem" }}>
                    +<Currency amount={item.sellerShippingFee} /> {t("products.shippingLabel")}
                  </Box>
                )}
              </Typography>

              <Box
                sx={{
                  mt: 1,
                  display: "inline-flex",
                  bgcolor: "grey.100",
                  px: 1,
                  py: 0.25,
                  borderRadius: 1,
                }}
              >
                <Typography
                  variant="caption"
                  fontWeight={600}
                  color="text.secondary"
                  className="notranslate"
                  translate="no"
                >
                  {t("orders.quantity", { count: item.quantity })}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    // --- MAIN LIST LAYOUT ---
    return (
      <Card
        variant="outlined"
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          p: 2,
          borderRadius: 2,
          bgcolor: "background.paper",
          border: "1px solid",
          borderColor: "divider",
          width: "100%",
          transition: "background-color 0.2s",
          "&:hover": { bgcolor: "grey.50" },
        }}
      >
        <Avatar
          src={item.image}
          variant="rounded"
          sx={{
            width: 56,
            height: 56,
            bgcolor: "white",
            border: "1px solid",
            borderColor: "grey.200",
            img: { objectFit: "contain", p: 0.5 },
          }}
        />

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="flex-start"
            gap={1}
          >
            <Typography
              variant="body2"
              sx={{
                fontWeight: 600,
                lineHeight: 1.3,
                mb: 0.5,
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
              }}
            >
              {item.title}
            </Typography>
            {showStatusChip && (
              <Chip
                label={
                  <span className="notranslate" translate="no">
                    {effectiveStatus.name}
                  </span>
                }
                size="small"
                color={getStatusColor(effectiveStatus.name)}
                sx={{ height: 20, fontSize: "0.65rem", fontWeight: 700 }}
              />
            )}
          </Box>

          <Typography
            translate="no"
            variant="body2"
            color="text.secondary"
            sx={{ mb: 1 }}
            className="notranslate"
          >
            {item.quantity} × <Currency amount={item.price} />
            {item.sellerShippingFee != null && item.sellerShippingFee > 0 && (
              <Box component="span" sx={{ ml: 0.5, color: "text.secondary", fontSize: "0.8rem" }}>
                +<Currency amount={item.sellerShippingFee} /> {t("products.shippingLabel")}
              </Box>
            )}
          </Typography>

          <Box sx={{ width: "100%" }}>
            <StatusTimeline
              currentStatusId={effectiveStatus.id}
              allStatuses={visibleItemStatuses}
            />
          </Box>
        </Box>
      </Card>
    );
  }
);
OrderItemRow.displayName = "OrderItemRow";

// --- Loading Skeleton ---
const OrderSkeleton = () => (
  <Card sx={{ borderRadius: 3, mb: 3 }}>
    <CardContent sx={{ p: 3 }}>
      <Box display="flex" justifyContent="space-between" mb={3}>
        <Box>
          <Skeleton width={120} height={32} />
          <Skeleton width={180} height={20} />
        </Box>
        <Box>
          <Skeleton width={80} height={32} />
        </Box>
      </Box>
      <Skeleton variant="rectangular" height={100} sx={{ borderRadius: 2 }} />
    </CardContent>
  </Card>
);

// --- Main Component ---

const ProfileOrders: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const navigate = useNavigate();
  const fullScreenDialog = useMediaQuery(theme.breakpoints.down("sm"));

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [displayedOrders, setDisplayedOrders] = useState<Order[]>([]);
  const [totalOrders, setTotalOrders] = useState<number>(0);
  const [hasMoreOrders, setHasMoreOrders] = useState(false);

  // Removed expandedOrder state since we are moving details to dialog
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const [itemStatuses, setItemStatuses] = useState<ItemStatus[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedFilter, setSelectedFilter] = useState<string>(DEFAULT_FILTER);
  const [filters, setFilters] = useState<OrderFilters>(() => ({
    sortBy: "createdAt",
    sortDescending: true,
    pageNumber: 1,
    pageSize: PAGE_SIZE,
    ...getDateRangeForFilter(DEFAULT_FILTER),
  }));

  // Refs to prevent duplicate API calls (React StrictMode in dev renders twice)
  const isInitialized = useRef(false);
  const isFetching = useRef(false);

  const fetchOrders = useCallback(async (isLoadMore = false) => {
    // Prevent duplicate calls
    if (isFetching.current) return;
    isFetching.current = true;

    if (isLoadMore) setLoadingMore(true);
    else setLoading(true);

    setError(null);

    try {
      const response = await getOrders(filters);

      if (response.isSuccess) {
        const { items, totalCount, totalPages, pageSize, hasNextPage } =
          response.data;
        const effectivePageSize = pageSize || filters.pageSize || PAGE_SIZE;
        const safeItems = items || [];

        const inferredTotal =
          typeof totalCount === "number" && totalCount > 0
            ? totalCount
            : typeof totalPages === "number" && totalPages > 0
              ? totalPages * effectivePageSize
              : 0;

        setTotalOrders((prev) =>
          isLoadMore
            ? Math.max(prev, inferredTotal)
            : inferredTotal || safeItems.length
        );

        if (isLoadMore) {
          setDisplayedOrders((prev) => {
            const existingIds = new Set(prev.map((o) => o.orderNumber));
            const uniqueNewItems = safeItems.filter(
              (o) => !existingIds.has(o.orderNumber)
            );
            return [...prev, ...uniqueNewItems];
          });
        } else {
          setDisplayedOrders(safeItems);
        }

        const receivedFullPage = safeItems.length === effectivePageSize;
        const apiHasNext =
          typeof hasNextPage === "boolean" ? hasNextPage : false;
        setHasMoreOrders(apiHasNext || receivedFullPage);
      } else {
        setError(formatApiError(response));
      }
    } catch (err) {
      setError(t("profile.fetchOrdersError"));
      console.error(err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      isFetching.current = false;
    }
  }, [filters]);

  // Combined initialization effect - runs once on mount
  useEffect(() => {
    // Prevent double execution in React StrictMode
    if (isInitialized.current) return;
    isInitialized.current = true;

    const initializeData = async () => {
      setLoading(true);

      // Fetch item statuses and orders in parallel
      const [statusesResult, ordersResult] = await Promise.allSettled([
        getItemStatuses(),
        getOrders(filters),
      ]);

      // Handle item statuses
      if (statusesResult.status === "fulfilled" && statusesResult.value.isSuccess) {
        setItemStatuses(statusesResult.value.data || []);
      }

      // Handle orders
      if (ordersResult.status === "fulfilled" && ordersResult.value.isSuccess) {
        const { items, totalCount, totalPages, pageSize, hasNextPage } = ordersResult.value.data;
        const effectivePageSize = pageSize || filters.pageSize || PAGE_SIZE;
        const safeItems = items || [];

        const inferredTotal =
          typeof totalCount === "number" && totalCount > 0
            ? totalCount
            : typeof totalPages === "number" && totalPages > 0
              ? totalPages * effectivePageSize
              : 0;

        setTotalOrders(inferredTotal || safeItems.length);
        setDisplayedOrders(safeItems);

        const receivedFullPage = safeItems.length === effectivePageSize;
        const apiHasNext = typeof hasNextPage === "boolean" ? hasNextPage : false;
        setHasMoreOrders(apiHasNext || receivedFullPage);

        // Determine available years from oldest order if we have orders
        // We use the current orders response instead of making a separate API call
        if (safeItems.length > 0) {
          // Find the oldest order date from current results
          const oldestDate = safeItems.reduce((oldest, order) => {
            const orderDate = new Date(order.createdAt);
            return orderDate < oldest ? orderDate : oldest;
          }, new Date());

          const currentYear = new Date().getFullYear();
          const firstYear = oldestDate.getFullYear();
          const years: number[] = [];
          for (let year = currentYear; year >= firstYear; year--) {
            years.push(year);
          }
          setAvailableYears(years);
        } else {
          setAvailableYears([new Date().getFullYear()]);
        }
      } else {
        setAvailableYears([new Date().getFullYear()]);
        if (ordersResult.status === "rejected" ||
            (ordersResult.status === "fulfilled" && !ordersResult.value.isSuccess)) {
          setError(t("profile.fetchOrdersFailed"));
        }
      }

      setLoading(false);
    };

    initializeData();
  }, []); // Empty dependency array - only run on mount

  // Track the previous filter to detect real changes
  const prevFiltersRef = useRef(filters);

  // Effect for filter changes (after initial load)
  useEffect(() => {
    // Skip on initial mount (handled by initializeData)
    if (!isInitialized.current) return;

    // Check if filters actually changed (not just a re-render)
    const filtersChanged =
      JSON.stringify(prevFiltersRef.current) !== JSON.stringify(filters);

    if (!filtersChanged) return;
    prevFiltersRef.current = filters;

    const isLoadMore = (filters.pageNumber || 1) > 1;
    fetchOrders(isLoadMore);
  }, [filters, fetchOrders]);

  const handleLoadMore = () =>
    setFilters((prev) => ({ ...prev, pageNumber: (prev.pageNumber || 1) + 1 }));

  const handleFilterChange = (value: string) => {
    if (value === selectedFilter) return;
    setSelectedFilter(value);
    const dateRange = getDateRangeForFilter(value);
    setDisplayedOrders([]);
    setHasMoreOrders(false);
    setFilters((prev) => ({
      ...prev,
      ...dateRange,
      pageNumber: 1,
      pageSize: PAGE_SIZE,
    }));
  };

  const handleOrderClick = useCallback((order: Order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  }, []);

  const toggleItemsExpansion = useCallback((orderNumber: string) => {
    setExpandedItems((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(orderNumber)) newSet.delete(orderNumber);
      else newSet.add(orderNumber);
      return newSet;
    });
  }, []);

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200, mx: "auto" }}>
      <Card sx={{ mb: 3, p: 2, borderRadius: 3, boxShadow: 1 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems={{ xs: "flex-start", sm: "center" }}
          spacing={2}
        >
          <Typography
            variant="h5"
            component="div"
            fontWeight={700}
            className="notranslate"
            translate="no"
          >
            {t("orders.yourOrders")}
          </Typography>

          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ width: { xs: "100%", sm: "auto" } }}
          >
            <Typography
              variant="body2"
              fontWeight={600}
              color="text.secondary"
              className="notranslate"
              translate="no"
            >
              {t("orders.filter")}
            </Typography>
            <FormControl
              size="small"
              sx={{ minWidth: 160, flex: { xs: 1, sm: "none" } }}
            >
              <Select
                value={selectedFilter}
                onChange={(e) => handleFilterChange(e.target.value as string)}
                displayEmpty
                className="notranslate"
                translate="no"
                sx={{ borderRadius: 2 }}
              >
                <MenuItem
                  value="past_30_days"
                  className="notranslate"
                  translate="no"
                >
                  {t("orders.past30Days")}
                </MenuItem>
                <MenuItem
                  value="past_3_months"
                  className="notranslate"
                  translate="no"
                >
                  {t("orders.past3Months")}
                </MenuItem>
                <Divider />
                {availableYears.map((year) => (
                  <MenuItem
                    key={year}
                    value={`year_${year}`}
                    className="notranslate"
                    translate="no"
                  >
                    {year}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </Stack>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
          <span className="notranslate" translate="no">
            {error}
          </span>
        </Alert>
      )}

      {loading && displayedOrders.length === 0 && (
        <Box>
          <OrderSkeleton />
          <OrderSkeleton />
        </Box>
      )}

      {!loading && displayedOrders.length === 0 && !error && (
        <Card
          sx={{
            py: 8,
            px: 2,
            textAlign: "center",
            borderRadius: 3,
            borderStyle: "dashed",
            borderColor: "divider",
          }}
          elevation={0}
        >
          <ShoppingCart
            sx={{ fontSize: 64, color: "action.disabled", mb: 2 }}
          />
          <Typography
            variant="h6"
            component="div"
            gutterBottom
            fontWeight={600}
            className="notranslate"
            translate="no"
          >
            {t("orders.noOrdersFound")}
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            maxWidth={400}
            mx="auto"
            className="notranslate"
            translate="no"
          >
            {t("orders.noOrdersDescription")}
          </Typography>
        </Card>
      )}

      <Stack spacing={3}>
        {displayedOrders.map((order) => (
          <Card
            key={order.orderNumber}
            sx={{
              borderRadius: 3,
              transition: "box-shadow 0.2s, transform 0.2s",
              "&:hover": { boxShadow: 6, transform: "translateY(-2px)" },
              overflow: "visible",
              border: "1px solid",
              borderColor: "divider",
            }}
            elevation={2}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Grid
                container
                spacing={2}
                justifyContent="space-between"
                alignItems="flex-start"
                sx={{ mb: 3 }}
              >
                <Grid item>
                  <Typography
                    variant="h6"
                    component="div"
                    fontWeight={800}
                    className="notranslate"
                    translate="no"
                    sx={{ mb: 0.5 }}
                  >
                    {t("orders.orderNumber", { number: order.orderNumber })}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <CalendarToday
                      sx={{ fontSize: 16, color: "text.secondary" }}
                    />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      className="notranslate"
                      translate="no"
                    >
                      {t("orders.placedOn", {
                        date: formatDate(order.createdAt),
                      })}
                    </Typography>
                  </Stack>
                </Grid>

                <Grid item sx={{ textAlign: { xs: "left", sm: "right" } }}>
                  <Typography
                    translate="no"
                    variant="h6"
                    component="div"
                    fontWeight={800}
                    color="text.primary"
                    className="notranslate"
                  >
                    <Currency amount={order.finalTotal} tjsAmount={order.finalTotalTjs} />
                  </Typography>
                  <Typography
                    variant="caption"
                    fontWeight={600}
                    color="text.secondary"
                    className="notranslate"
                    translate="no"
                    sx={{ textTransform: "uppercase" }}
                  >
                    {order.items.length === 1
                      ? t("orders.itemsCount", { count: order.items.length })
                      : t("orders.itemsCountPlural", {
                          count: order.items.length,
                        })}
                  </Typography>
                  {/* Payment Status & Method Chips */}
                  <Stack
                    direction="row"
                    spacing={0.5}
                    sx={{ mt: 1, justifyContent: { xs: "flex-start", sm: "flex-end" }, flexWrap: "wrap", gap: 0.5 }}
                  >
                    {order.paymentStatus && (
                      <Chip
                        icon={<Payment sx={{ fontSize: 14 }} />}
                        label={
                          <span className="notranslate" translate="no">
                            {(() => { const key = getPaymentStatusLabelKey(order.paymentStatus); return key ? t(key) : (order.paymentStatus || t("orders.paymentStatusLabels.unknown")); })()}
                          </span>
                        }
                        size="small"
                        color={getPaymentStatusColor(order.paymentStatus)}
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
                      />
                    )}
                    {order.paymentMethod && (
                      <Chip
                        icon={<CreditCard sx={{ fontSize: 14 }} />}
                        label={
                          <span className="notranslate" translate="no">
                            {order.paymentMethod}
                          </span>
                        }
                        size="small"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
                      />
                    )}
                    {order.promotionCode && (
                      <Chip
                        icon={<Discount sx={{ fontSize: 14 }} />}
                        label={
                          <span className="notranslate" translate="no">
                            {order.promotionCode}
                          </span>
                        }
                        size="small"
                        color="success"
                        variant="outlined"
                        sx={{ height: 22, fontSize: "0.7rem", fontWeight: 600 }}
                      />
                    )}
                  </Stack>
                </Grid>
              </Grid>

              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2}>
                  {order.items
                    .slice(
                      0,
                      expandedItems.has(order.orderNumber) ? undefined : 3
                    )
                    .map((item, idx) => (
                      <Grid item xs={12} key={idx}>
                        <OrderItemRow
                          item={item}
                          itemStatuses={itemStatuses}
                          orderStatus={order.orderStatus}
                        />
                      </Grid>
                    ))}

                  {order.items.length > 3 && (
                    <Grid item xs={12}>
                      <Button
                        fullWidth
                        onClick={() => toggleItemsExpansion(order.orderNumber)}
                        className="notranslate"
                        translate="no"
                        sx={{
                          bgcolor: "action.hover",
                          color: "text.secondary",
                          py: 1.5,
                          borderRadius: 2,
                          borderStyle: "dashed",
                          borderWidth: 1,
                          borderColor: "divider",
                          textTransform: "none",
                        }}
                      >
                        {expandedItems.has(order.orderNumber)
                          ? t("orders.showLess")
                          : t("orders.viewMoreItems", {
                              count: order.items.length - 3,
                            })}
                      </Button>
                    </Grid>
                  )}
                </Grid>
              </Box>

              <Divider sx={{ my: 2 }} />

              <Box display="flex" justifyContent="flex-end" alignItems="center" gap={1}>
                {/* Leave Review button - shown for delivered orders */}
                {order.orderStatus?.toLowerCase() === "delivered" && (
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RateReview />}
                    onClick={() => navigate(`/review/order/${order.orderId}`)}
                    className="notranslate"
                    translate="no"
                    sx={{
                      borderRadius: 2,
                      textTransform: "none",
                      fontWeight: 600,
                      px: 2,
                      borderColor: amazonColors.orange,
                      color: amazonColors.orange,
                      "&:hover": {
                        borderColor: amazonColors.orangeHover || "#f0c14b",
                        bgcolor: "rgba(255, 153, 0, 0.04)",
                      },
                    }}
                  >
                    {t("orders.leaveReview")}
                  </Button>
                )}
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<Receipt />}
                  onClick={() => handleOrderClick(order)}
                  className="notranslate"
                  translate="no"
                  sx={{
                    borderRadius: 2,
                    textTransform: "none",
                    fontWeight: 800,
                    px: 3,
                    bgcolor: amazonColors.orange,
                    color: "#fff",
                    "&:hover": {
                      bgcolor: amazonColors.orangeHover || "#f0c14b",
                    },
                  }}
                >
                  {t("orders.orderDetails")}
                </Button>
              </Box>

              {/* Removed the expandedOrder collapsible section here */}
            </CardContent>
          </Card>
        ))}
      </Stack>

      {hasMoreOrders && (
        <Box sx={{ display: "flex", justifyContent: "center", mt: 4, mb: 2 }}>
          <Button
            variant="outlined"
            onClick={handleLoadMore}
            disabled={loadingMore}
            className="notranslate"
            translate="no"
            startIcon={
              loadingMore ? (
                <CircularProgress size={20} color="inherit" />
              ) : null
            }
            sx={{
              px: 4,
              py: 1.5,
              borderRadius: 10,
              textTransform: "none",
              fontWeight: 600,
              minWidth: 200,
            }}
          >
            {loadingMore
              ? t("orders.loadingMoreOrders")
              : t("orders.loadMoreOrders")}
          </Button>
        </Box>
      )}

      {/* --- ORDER DETAILS DIALOG --- */}
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={fullScreenDialog}
        PaperProps={{
          sx: { borderRadius: fullScreenDialog ? 0 : 2 },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            borderBottom: "1px solid",
            borderColor: "grey.200",
            bgcolor: "grey.50",
            px: 3,
            py: 2,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              component="div"
              fontWeight={700}
              className="notranslate"
              translate="no"
              sx={{ lineHeight: 1.2 }}
            >
              {t("orders.orderDetails")}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              className="notranslate"
              translate="no"
            >
              {t("orders.orderedOn", {
                date: selectedOrder ? formatDate(selectedOrder.createdAt) : "",
              })}
            </Typography>
          </Box>
          <IconButton onClick={() => setDialogOpen(false)}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0, bgcolor: "white" }}>
          {selectedOrder && (
            <Box>
              {/* Summary Section moved inside Dialog */}
              <Box
                sx={{
                  p: 3,
                  bgcolor: "grey.50",
                  borderBottom: "1px solid",
                  borderColor: "grey.200",
                }}
              >
                <Grid container spacing={3}>
                  {/* Pick Up Location + Billing Address Column */}
                  <Grid item xs={12} sm={6}>
                    {/* Pick Up Location */}
                    {selectedOrder.pickUpLocation && (
                      <>
                        <Typography
                          variant="subtitle2"
                          fontWeight={700}
                          gutterBottom
                          className="notranslate"
                          translate="no"
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocalShipping fontSize="small" color="action" />{" "}
                          {t("orders.shippingAddress")}
                        </Typography>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          className="notranslate"
                          translate="no"
                          sx={{ pl: 3.5, mb: 1.5 }}
                        >
                          {selectedOrder.pickUpLocation}
                        </Typography>
                      </>
                    )}

                    {/* Billing Address */}
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      gutterBottom
                      className="notranslate"
                      translate="no"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Receipt fontSize="small" color="action" />{" "}
                      {t("orders.billingAddress")}
                    </Typography>
                    <Box sx={{ pl: 3.5 }}>
                      <Typography
                        variant="body2"
                        fontWeight={600}
                        className="notranslate"
                        translate="no"
                      >
                        {selectedOrder.deliveryInfo.address}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="notranslate"
                        translate="no"
                      >
                        {selectedOrder.deliveryInfo.city},{" "}
                        {selectedOrder.deliveryInfo.state}{" "}
                        {selectedOrder.deliveryInfo.zipCode}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        className="notranslate"
                        translate="no"
                      >
                        {selectedOrder.deliveryInfo.country}
                      </Typography>
                    </Box>
                  </Grid>

                  {/* Payment Summary Column */}
                  <Grid item xs={12} sm={6}>
                    <Typography
                      variant="subtitle2"
                      fontWeight={700}
                      gutterBottom
                      className="notranslate"
                      translate="no"
                      sx={{ display: "flex", alignItems: "center", gap: 1 }}
                    >
                      <Description fontSize="small" color="action" />{" "}
                      {t("orders.orderSummary")}
                    </Typography>
                    <Stack
                      spacing={1}
                      sx={{ pl: 3.5, maxWidth: 300 }}
                      className="notranslate"
                      translate="no"
                    >
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("orders.subtotal")}
                        </Typography>
                        <Typography translate="no" variant="body2">
                          <Currency amount={selectedOrder.totalAmount} tjsAmount={selectedOrder.totalAmountTjs} />
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("orders.taxes")}
                        </Typography>
                        <Typography translate="no" variant="body2">
                          <Currency amount={selectedOrder.tax ?? 0} />
                        </Typography>
                      </Box>
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="body2" color="text.secondary">
                          {t("orders.fees")}
                        </Typography>
                        <Typography translate="no" variant="body2">
                          <Currency amount={selectedOrder.fees ?? 0} />
                        </Typography>
                      </Box>
                      {/* Promo Code Discount */}
                      {selectedOrder.promotionCode && (selectedOrder.promotionDiscountAmount ?? 0) > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <Discount fontSize="small" sx={{ color: "success.main", fontSize: 16 }} />
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              {t("orders.promoCode")}: {selectedOrder.promotionCode}
                            </Typography>
                          </Box>
                          <Typography translate="no" variant="body2" color="success.main" fontWeight={600}>
                            -<Currency amount={selectedOrder.promotionDiscountAmount} />
                          </Typography>
                        </Box>
                      )}
                      {/* Payment Method Discount */}
                      {(selectedOrder.paymentMethodDiscountAmount ?? 0) > 0 && (
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <CreditCard fontSize="small" sx={{ color: "success.main", fontSize: 16 }} />
                            <Typography variant="body2" color="success.main" fontWeight={600}>
                              {t("orders.paymentDiscount")}
                            </Typography>
                          </Box>
                          <Typography translate="no" variant="body2" color="success.main" fontWeight={600}>
                            -<Currency amount={selectedOrder.paymentMethodDiscountAmount} />
                          </Typography>
                        </Box>
                      )}
                      <Divider sx={{ my: 0.5 }} />
                      <Box display="flex" justifyContent="space-between">
                        <Typography variant="subtitle2" fontWeight={700}>
                          {t("orders.total")}
                        </Typography>
                        <Typography
                          translate="no"
                          variant="subtitle2"
                          fontWeight={700}
                          color="text.primary"
                        >
                          <Currency amount={selectedOrder.finalTotal} tjsAmount={selectedOrder.finalTotalTjs} />
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        sx={{ mt: 0.5, gap: 2 }}
                      >
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <LocalShipping
                            fontSize="small"
                            sx={{ color: "text.secondary" }}
                          />
                          <Typography variant="body2" color="text.secondary">
                            {t("orderConfirmation.shippingDueAtPickup")}
                          </Typography>
                        </Box>
                        <Typography translate="no" variant="body2">
                          <Currency amount={selectedOrder.shippingCost} tjsAmount={selectedOrder.shippingCostTjs} />
                        </Typography>
                      </Box>
                      {/* Payment Info */}
                      {(selectedOrder.paymentMethod || selectedOrder.paymentStatus) && (
                        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: "1px dashed", borderColor: "grey.300" }}>
                          <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {selectedOrder.paymentStatus && (
                              <Chip
                                icon={<Payment sx={{ fontSize: 14 }} />}
                                label={(() => { const key = getPaymentStatusLabelKey(selectedOrder.paymentStatus); return key ? t(key) : (selectedOrder.paymentStatus || t("orders.paymentStatusLabels.unknown")); })()}
                                size="small"
                                color={getPaymentStatusColor(selectedOrder.paymentStatus)}
                                sx={{ height: 24, fontSize: "0.75rem", fontWeight: 600 }}
                              />
                            )}
                            {selectedOrder.paymentMethod && (
                              <Chip
                                icon={<CreditCard sx={{ fontSize: 14 }} />}
                                label={selectedOrder.paymentMethod}
                                size="small"
                                variant="outlined"
                                sx={{ height: 24, fontSize: "0.75rem", fontWeight: 600 }}
                              />
                            )}
                          </Stack>
                        </Box>
                      )}
                    </Stack>
                  </Grid>
                </Grid>
              </Box>

              {/* Items List */}
              <Box sx={{ px: 3, py: 2 }}>
                <Typography
                  variant="h6"
                  component="div"
                  fontWeight={700}
                  gutterBottom
                  className="notranslate"
                  translate="no"
                >
                  {t("orders.shipmentDetails")}
                </Typography>
                <Stack spacing={0}>
                  {selectedOrder.items.map((item, index) => (
                    <OrderItemRow
                      key={index}
                      item={item}
                      itemStatuses={itemStatuses}
                      isDialog={true}
                      orderStatus={selectedOrder.orderStatus}
                    />
                  ))}
                </Stack>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions
          sx={{
            p: 2,
            borderTop: "1px solid",
            borderColor: "divider",
            bgcolor: "grey.50",
          }}
        >
          <Button
            onClick={() => setDialogOpen(false)}
            variant="contained"
            className="notranslate"
            translate="no"
            sx={{
              bgcolor: amazonColors.orange,
              color: "#fff",
              fontWeight: 800,
              px: 4,
              "&:hover": { bgcolor: amazonColors.orangeHover || "#f0c14b" },
            }}
          >
            {t("orders.close")}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ProfileOrders;
